import { computeProductsExportFileName } from '@app/utils/helpers';
import { useGetProductsCSVExport } from '@shared/api/hooks/pcf/products';
import {
  AppProductsListEmissionsCategoryItem,
  AppProductsListSortItem,
  StatusEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { Button } from '@shared/components/ui/button';
import sentry from '@shared/services/sentry';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productTableSchema } from './product-table';

interface ProductsTableToolbarActionsProps {
  collectionUuid?: string;
}

export function ProductsTableToolbarActions({
  collectionUuid,
}: ProductsTableToolbarActionsProps) {
  const [enableDownload, setEnableDownload] = useState(false);

  const [searchParams] = useSearchParams();

  const emissionsCategory = searchParams
    .get('emissionsCategory')
    ?.split('.') as AppProductsListEmissionsCategoryItem[];

  const search = productTableSchema.parse(Object.fromEntries(searchParams));
  const status = searchParams.get('status')?.split('.') as StatusEnum[];

  const { refetch } = useGetProductsCSVExport(enableDownload, {
    collection: collectionUuid,
    emissions_category: emissionsCategory,
    search: search.name ?? undefined,
    sort: [search.sort] as AppProductsListSortItem[],
    status: status,
  });

  const onProductsCSVExport = async () => {
    try {
      setEnableDownload(true);
      const { data } = await refetch();
      const filename = computeProductsExportFileName();
      const tempLink = document.createElement('a');
      tempLink.setAttribute('download', `${filename}.csv`);
      const universalBOM = '\uFEFF';
      tempLink.href = `data:text/csv;charset=utf-8,${encodeURIComponent(
        universalBOM + data
      )}`;
      tempLink.click();
    } catch (error) {
      sentry.log(error);
    } finally {
      setEnableDownload(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        data-testid="download-products-button"
        onClick={onProductsCSVExport}
        icon={<Icons.download className="mr-2 size-4" aria-hidden="true" />}
        loading={enableDownload}
        disabled={enableDownload}
      >
        Download
      </Button>
    </div>
  );
}
